import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, FileText, BarChart3, Download, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { coiAPI, analysisAPI } from '../services/api';

/**
 * CoiResults page — route: /analysis/:id/coi-results
 *
 * Displays the output of a completed COI / Eukaryota (QIIME 2) analysis.
 * Shows three sections: CSV files, .qzv visualizations, and R figures.
 * Re-uses the generic /analysis/:id/file/:filename endpoint for downloads.
 */
export default function CoiResults() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [imageUrls, setImageUrls] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await coiAPI.getResults(id);
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (!cancelled)
          setError(err.response?.data?.detail || 'Failed to load COI results.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Fetch figure PNGs via the authenticated API and expose them as blob URLs
  // so <img src> works (browsers can't attach Authorization headers to <img>).
  useEffect(() => {
    const pngs = (data?.figures || []).filter((f) => f.type === 'png');
    if (!pngs.length) return undefined;

    let cancelled = false;
    const created = {};
    (async () => {
      for (const f of pngs) {
        try {
          const res = await analysisAPI.downloadResultFile(id, f.filename);
          if (cancelled) return;
          const blob = new Blob([res.data], { type: 'image/png' });
          created[f.filename] = URL.createObjectURL(blob);
        } catch {
          /* silently skip; the download button (qzv) still works */
        }
      }
      if (!cancelled) setImageUrls((prev) => ({ ...prev, ...created }));
    })();

    return () => {
      cancelled = true;
      Object.values(created).forEach((u) => URL.revokeObjectURL(u));
    };
  }, [data, id]);

  const fileUrl = (filename) => analysisAPI.getResultFileUrl(id, filename); // eslint-disable-line no-unused-vars

  const downloadFile = async (filename, displayName) => {
    try {
      const res = await analysisAPI.downloadResultFile(id, filename);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = displayName || filename.split('/').pop();
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError(`Failed to download ${displayName || filename}`);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-12 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-3 text-muted-foreground">Loading COI results...</span>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-5xl">
      <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-4">
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Dashboard
      </Button>

      <h1 className="text-3xl font-bold mb-2">
        COI / Eukaryota Results — Analysis #{id}
      </h1>
      {data?.project_name && (
        <p className="text-muted-foreground mb-6">Project: {data.project_name}</p>
      )}

      {error && (
        <div className="bg-destructive/10 border border-destructive/20 text-destructive p-4 rounded-lg mb-6">
          {error}
        </div>
      )}

      {/* CSV files */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <FileText className="h-4 w-4" />
            CSV Tables ({data?.csv_files?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {!data?.csv_files?.length && (
            <p className="text-sm text-muted-foreground">No CSV files yet.</p>
          )}
          {data?.csv_files?.map((f) => (
            <div
              key={f.filename}
              className="flex items-center justify-between bg-muted/40 px-3 py-2 rounded-md"
            >
              <span className="text-sm">{f.display_name}</span>
              <Button size="sm" variant="outline" onClick={() => downloadFile(f.filename, f.display_name)}>
                <Download className="h-4 w-4 mr-1.5" />
                Download
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* QIIME .qzv visualizations — show rendered PNG preview at top, .qzv downloads below */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <BarChart3 className="h-4 w-4" />
            QIIME 2 Visualizations ({data?.qzv_files?.length || 0})
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Rendered preview below. Download any <code>.qzv</code> to explore interactively at{' '}
            <a
              href="https://view.qiime2.org/"
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              view.qiime2.org
            </a>
            .
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* PNG previews (relative-abundance barplot etc.) rendered inline.
              We use blob URLs from authenticated fetches so <img src> works. */}
          {data?.figures?.filter((f) => f.type === 'png').map((f) => {
            const blobUrl = imageUrls[f.filename];
            if (!blobUrl) {
              return (
                <div
                  key={f.filename}
                  className="border rounded-md p-4 bg-muted/30 text-sm text-muted-foreground flex items-center gap-2"
                >
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading {f.display_name}…
                </div>
              );
            }
            return (
              <button
                key={f.filename}
                type="button"
                onClick={() => window.open(blobUrl, '_blank', 'noopener,noreferrer')}
                className="block w-full text-left border rounded-md overflow-hidden hover:border-primary transition-colors"
              >
                <img
                  src={blobUrl}
                  alt={f.display_name}
                  className="w-full max-h-[600px] object-contain bg-muted/30"
                />
                <div className="px-3 py-2 text-sm border-t flex items-center justify-between">
                  <span>{f.display_name}</span>
                  <span
                    role="link"
                    onClick={(e) => {
                      e.stopPropagation();
                      downloadFile(f.filename, f.display_name);
                    }}
                    className="inline-flex items-center gap-1 text-xs underline hover:text-primary"
                  >
                    <Download className="h-3 w-3" />
                    Download PNG
                  </span>
                </div>
              </button>
            );
          })}

          {/* Interactive HTML figures (e.g. plotly) as download links */}
          {data?.figures?.filter((f) => f.type === 'html').map((f) => (
            <div
              key={f.filename}
              className="flex items-center justify-between bg-muted/40 px-3 py-2 rounded-md"
            >
              <span className="text-sm">{f.display_name}</span>
              <Button size="sm" variant="outline" onClick={() => downloadFile(f.filename, f.display_name)}>
                <Download className="h-4 w-4 mr-1.5" />
                Download
              </Button>
            </div>
          ))}

          {/* .qzv download list */}
          {!data?.qzv_files?.length && (
            <p className="text-sm text-muted-foreground">No QIIME visualizations yet.</p>
          )}
          {data?.qzv_files?.map((f) => (
            <div
              key={f.filename}
              className="flex items-center justify-between bg-muted/40 px-3 py-2 rounded-md"
            >
              <span className="text-sm">{f.display_name}</span>
              <Button size="sm" variant="outline" onClick={() => downloadFile(f.filename, f.display_name)}>
                <Download className="h-4 w-4 mr-1.5" />
                Download
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
